import React from "react";

const WhatPlex = () => (
  <section id="what-plex" className="developer-section">
    <h2>What Plex Does</h2>
    <p>
      Plex is a biomedical knowledge graph platform that unifies heterogeneous data sources into
      a single queryable graph. It exists because researchers and developers need to traverse
      complex relationships&mdash;from a compound to its targets, from targets to associated diseases,
      from diseases to relevant publications&mdash;without manually querying dozens of databases.
    </p>

    <h3>Architecture Overview</h3>
    <div className="dev-architecture">
      <pre className="dev-architecture-diagram">{`
  Your Application          Plex API Server           Knowledge Graph
  ================        ==================        ==================

  Query Entities   ------>  /api/search       ------>  Entity Resolution
  (IDs or names)            |                          |
                            v                          v
  SSE Stream       <------  /api/stream/{id}   <------  Graph Traversal
  (real-time)               |                          |
                            v                          v
  Results          <------  Ranked Results    <------  Similarity Scoring
  (by category)             + Evidence Chains          + Evidence Chains
      `}</pre>
    </div>

    <h3>Entity Types</h3>
    <p>
      The knowledge graph contains these primary entity categories. Retrieve the full
      list for your deployment via{" "}
      <code>GET /api/categories</code>:
    </p>
    <table className="dev-api-params">
      <thead>
        <tr><th>Category</th><th>Examples</th><th>Key Fields</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><code>compound</code></td>
          <td>Small molecules, drugs</td>
          <td>SMILES, InChI, molecular weight, ChEMBL ID</td>
        </tr>
        <tr>
          <td><code>target</code></td>
          <td>Proteins, gene products</td>
          <td>Gene symbol, UniProt ID, organism</td>
        </tr>
        <tr>
          <td><code>disease</code></td>
          <td>Conditions, indications</td>
          <td>MeSH terms, ontology IDs</td>
        </tr>
        <tr>
          <td><code>pathway</code></td>
          <td>Biological pathways</td>
          <td>KEGG ID, Reactome ID, organism</td>
        </tr>
        <tr>
          <td><code>publication</code></td>
          <td>Journal articles</td>
          <td>PubMed ID, DOI, authors, journal</td>
        </tr>
        <tr>
          <td><code>patent</code></td>
          <td>Patent documents</td>
          <td>Patent number, filing date, assignee</td>
        </tr>
      </tbody>
    </table>
    <pre className="dev-code-pre"><code>{`curl -s /api/categories -H "x-api-key: $PLEX_TOKEN" | jq .`}</code></pre>

    <h3>How a Search Works</h3>
    <ol className="dev-steps-list">
      <li>
        <strong>Entity Resolution</strong> &mdash; You provide entity IDs (e.g., <code>unichem:161671</code>) or
        free-text names that Plex resolves via the suggestions endpoint.
      </li>
      <li>
        <strong>Graph Traversal</strong> &mdash; The server traverses the knowledge graph from your query entities,
        finding connected entities across all categories with similarity scoring.
      </li>
      <li>
        <strong>Ranked Results</strong> &mdash; Results are returned grouped by category (targets, compounds, diseases, etc.),
        ranked by relevance/similarity score.
      </li>
      <li>
        <strong>Evidence Chains</strong> &mdash; For any result, you can request evidence chains showing the
        multi-hop path connecting your query to that result, with supporting datasets.
      </li>
    </ol>

    <h3>Response Structure</h3>
    <p>Every search returns results organized by category with pagination support:</p>
    <pre className="dev-code-pre"><code>{`{
  "results": {
    "target": [
      {"rank": 0.95, "neighborhood": "3", "neighborhood_size": 12, "direct_connections": 1,
       "entity": {"id": "entrezgene:1956", "title": "EGFR", ...}},
      {"rank": 0.87, "neighborhood": "3", "neighborhood_size": 12, "direct_connections": 1,
       "entity": {"id": "entrezgene:1017", "title": "CDK2", ...}}
    ],
    "compound": [...],
    "disease": [...],
    "publication": [...]
  },
  "totals": {"target": 142, "compound": 89, "disease": 23, "publication": 456},
  "page_token": "next-page-cursor"
}`}</code></pre>
  </section>
);

export default WhatPlex;
